/**
 * Información del negocio (spec 038): etiquetas y el puente hacia la ficha del alta.
 */
import type { FichaAlta } from './altas'

export interface ContactoVista {
    email?: string
    address?: string
    phone?: string
    parking?: string
    transport?: string
    website?: string
    extraInfo?: string
    nAdministradores?: number
}

export interface ProfesionalAsignacion {
    id: number
    nombre: string
}

export interface VistaInformacion {
    identidad: {
        name: string | null
        timezone: string | null
        estado: string | null
        nombreVisible: string | null
    }
    reglas: Record<string, unknown> | null
    vocabulario: Record<string, unknown> | null
    voz: Record<string, unknown> | null
    conocimiento: Record<string, unknown> | null
    escalada: Record<string, unknown> | null
    capacidades: Record<string, unknown> | null
    legal: Record<string, unknown> | null
    instruccionesExtra: string | null
    publicarPrecios: boolean | null
    leerNotasPrivadas: boolean | null
    profesionalesParaAsignacion: ProfesionalAsignacion[]
    /** Solo los nombres, para la bienvenida genérica. */
    nombresServicio: string[]
}

export interface InformacionRespuesta {
    accountId: number
    contacto: ContactoVista
    ficha: VistaInformacion | null
    motivoFicha: string | null
    /** Spec 052: E.164 del inbox WhatsApp, o null. Sin tokens. */
    whatsapp?: { e164: string | null }
}

const ESTADOS = ['demo', 'en_prueba', 'activo', 'pausado'] as const
const TRATOS = ['tu', 'usted', 'espejo'] as const
const EXTENSIONES = ['breve', 'normal', 'amplia'] as const
const EMOJIS = ['ninguno', 'alguno', 'soltura'] as const
const IDIOMAS = ['es', 'en', 'it'] as const
const CRITERIOS = ['masLiberado', 'aleatorio', 'fijo'] as const

function uno<T extends string>(v: unknown, permitidos: readonly T[], fb: T): T {
    return typeof v === 'string' && (permitidos as readonly string[]).includes(v) ? (v as T) : fb
}

function num(v: unknown, fb: number): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : fb
}

function bool(v: unknown, fb: boolean): boolean {
    return typeof v === 'boolean' ? v : fb
}

function str(v: unknown, fb = ''): string {
    return typeof v === 'string' ? v : fb
}

/** Catálogo y horario semanal tienen pestaña. El paso WhatsApp sí se enseña (052). */
export const PASOS_OMITIDOS_INFORMACION = ['catalogo'] as const

export function textoLineaWhatsapp(e164: string | null | undefined): string {
    return e164 && e164.trim() ? e164 : 'sin WhatsApp'
}

export function numeroDe(reglas: Record<string, unknown> | null, clave: string): number | null {
    if (!reglas) return null
    const v = reglas[clave]
    return typeof v === 'number' && Number.isFinite(v) ? v : null
}

export function textoDe(bloque: Record<string, unknown> | null, clave: string): string | null {
    if (!bloque) return null
    const v = bloque[clave]
    return typeof v === 'string' && v.trim() !== '' ? v : null
}

export function fichaAltaDesdeVista(v: VistaInformacion): FichaAlta {
    const voc = v.vocabulario ?? {}
    const voz = v.voz ?? {}
    const reglas = v.reglas ?? {}
    const asg = (
        reglas['asignacion'] && typeof reglas['asignacion'] === 'object'
            ? (reglas['asignacion'] as Record<string, unknown>)
            : {}
    )
    const criterio = uno(asg['criterio'], CRITERIOS, 'masLiberado')
    const pros = v.profesionalesParaAsignacion
    let profesionalIndice = 0
    if (criterio === 'fijo' && typeof asg['profesionalId'] === 'number') {
        const i = pros.findIndex((p) => p.id === asg['profesionalId'])
        if (i >= 0) profesionalIndice = i
    } else if (criterio === 'fijo' && typeof asg['profesionalIndice'] === 'number') {
        profesionalIndice = asg['profesionalIndice']
    }

    const conc = (v.capacidades?.['concedidas'] ?? {}) as Record<string, unknown>
    const act = (v.capacidades?.['activas'] ?? conc) as Record<string, unknown>
    const mapa = (src: Record<string, unknown>, escalar: boolean) => ({
        reservar: bool(src['reservar'], true),
        modificar: bool(src['modificar'], true),
        cancelar: bool(src['cancelar'], true),
        responder: bool(src['responder'], true),
        recordar: bool(src['recordar'], false),
        escalar,
    })

    const esc = v.escalada ?? {}
    const disp = (esc['disparadores'] ?? {}) as Record<string, unknown>
    const con = v.conocimiento ?? {}
    const ubi = (con['ubicacion'] && typeof con['ubicacion'] === 'object'
        ? (con['ubicacion'] as Record<string, unknown>)
        : null)
    const faqRaw = Array.isArray(con['faq']) ? con['faq'] : []
    const idiomasRaw = Array.isArray(voz['idiomas']) ? voz['idiomas'] : ['es']
    const idiomas = idiomasRaw.filter((x): x is (typeof IDIOMAS)[number] =>
        typeof x === 'string' && (IDIOMAS as readonly string[]).includes(x),
    ) as Array<(typeof IDIOMAS)[number]>

    const legal = v.legal ?? {}

    return {
        estado: uno(v.identidad.estado, ESTADOS, 'demo'),
        nombreVisible: v.identidad.nombreVisible ?? v.identidad.name ?? '',
        vocabulario: {
            cliente: str(voc['cliente'], 'cliente'),
            cita: str(voc['cita'], 'cita'),
            profesional: str(voc['profesional'], 'profesional'),
            servicio: str(voc['servicio'], 'servicio'),
        },
        voz: {
            tratamiento: uno(voz['tratamiento'], TRATOS, 'tu'),
            extension: uno(voz['extension'], EXTENSIONES, 'normal'),
            emojis: uno(voz['emojis'], EMOJIS, 'alguno'),
            presentacion: str(voz['presentacion']),
            idiomas: idiomas.length > 0 ? idiomas : ['es'],
            nuncaDecir: str(voz['nuncaDecir']),
        },
        capacidades: { concedidas: mapa(conc, true), activas: mapa(act, true) },
        escalada: {
            disparadores: {
                queja: bool(disp['queja'], true),
                precioFueraDeLoNormal: bool(disp['precioFueraDeLoNormal'], true),
                urgencia: bool(disp['urgencia'], true),
                datosDeSalud: bool(disp['datosDeSalud'], true),
            },
            aclaracionesAntesDeEscalar: num(esc['aclaracionesAntesDeEscalar'], 2),
        },
        conocimiento: {
            ...(ubi && str(ubi['direccion'])
                ? {
                      ubicacion: {
                          direccion: str(ubi['direccion']),
                          ...(str(ubi['indicaciones']) ? { indicaciones: str(ubi['indicaciones']) } : {}),
                      },
                  }
                : {}),
            faq: faqRaw
                .filter((f): f is Record<string, unknown> => typeof f === 'object' && f !== null)
                .map((f) => ({ pregunta: str(f['pregunta']), respuesta: str(f['respuesta']) })),
            ...(str(con['telefono']) ? { telefono: str(con['telefono']) } : {}),
            ...(str(con['bienvenida']) ? { bienvenida: str(con['bienvenida']) } : {}),
            ...(str(con['siempre']) ? { siempre: str(con['siempre']) } : {}),
        },
        reglas: {
            antelacionMinimaReservaMin: num(reglas['antelacionMinimaReservaMin'], 0),
            horizonteMaximoDias: num(reglas['horizonteMaximoDias'], 1),
            permiteMismoDia: bool(reglas['permiteMismoDia'], false),
            maxCitasActivasPorCliente: num(reglas['maxCitasActivasPorCliente'], 1),
            antelacionCancelacionHoras: num(reglas['antelacionCancelacionHoras'], 0),
            asignacion:
                criterio === 'fijo'
                    ? { criterio, profesionalIndice }
                    : { criterio },
        },
        catalogo: {
            servicios: (v.nombresServicio ?? []).map((nombre) => ({
                nombre,
                duracionMin: 0,
                margenMin: 0,
            })),
            profesionales: pros.map((p) => ({ nombre: p.nombre, serviciosIndices: [] })),
        },
        legal: {
            privacyPolicyUrl: str(legal['privacyPolicyUrl']),
            privacyPolicyDate: str(legal['privacyPolicyDate']),
            ...(str(legal['lateCancelPolicyMsg'])
                ? { lateCancelPolicyMsg: str(legal['lateCancelPolicyMsg']) }
                : {}),
        },
        publicarPrecios: bool(v.publicarPrecios, false),
        leerNotasPrivadas: bool(v.leerNotasPrivadas, false),
        ...(v.instruccionesExtra ? { instruccionesExtra: v.instruccionesExtra } : {}),
    }
}

function frasesLimpias(c: FichaAlta['conocimiento']): FichaAlta['conocimiento'] {
    const { bienvenida, siempre, ...resto } = c
    const b = bienvenida?.trim()
    const s = siempre?.trim()
    return {
        ...resto,
        ...(b ? { bienvenida: b } : {}),
        ...(s ? { siempre: s } : {}),
    }
}

/** La mitad que el provisioner re-proyecta. Sin catálogo: esa pestaña es otra. */
export function autoradaDesdeAlta(
    f: FichaAlta,
    profesionales: ProfesionalAsignacion[],
): Record<string, unknown> {
    const asg = f.reglas.asignacion
    const asignacion =
        asg.criterio === 'fijo'
            ? {
                  criterio: 'fijo' as const,
                  profesionalId: profesionales[asg.profesionalIndice ?? 0]?.id,
              }
            : { criterio: asg.criterio }
    return {
        estado: f.estado,
        nombreVisible: f.nombreVisible,
        vocabulario: f.vocabulario,
        voz: f.voz,
        capacidades: f.capacidades,
        escalada: f.escalada,
        conocimiento: frasesLimpias(f.conocimiento),
        reglas: { ...f.reglas, asignacion },
        legal: f.legal,
        publicarPrecios: f.publicarPrecios,
        leerNotasPrivadas: f.leerNotasPrivadas,
        ...(f.instruccionesExtra ? { instruccionesExtra: f.instruccionesExtra } : {}),
    }
}
